import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, distinctUntilChanged, filter, fromEvent, interval, map, shareReplay, switchMap, tap } from 'rxjs';
import { AuthService } from 'src/app/core/authentication/auth.service';

@Injectable({
  providedIn: 'root',
})
export class LogoutService {
  private authService = inject(AuthService);
  private readonly storageKey = 'talentpoint.pl:noActiveUserLogoutTime';
  private readonly countdownTrigger$ = new BehaviorSubject<number | null>(null);

  readonly timeToLogout$: Observable<number>;
  timeToLogout = 0;

  constructor() {
    this.timeToLogout$ = this.createCountdown();
    this.listenToStorageChanges();
  }

  startLogoutCountDown(ttl: number): void {
    this.countdownTrigger$.next(ttl);
    this.storeTTL(ttl);
  }

  restartCountdown(): void {
    const ttl = this.countdownTrigger$.value;
    if (ttl == null) {
      return;
    }
    this.storeTTL(ttl);
  }

  cancelLogout(): void {
    this.countdownTrigger$.next(null);
    localStorage.removeItem(this.storageKey);
  }

  private createCountdown(): Observable<number> {
    return this.countdownTrigger$.pipe(
      switchMap((ttl) => {
        if (ttl == null) return EMPTY;

        return interval(1000).pipe(
          map(() => this.getRemainingMs()),
          distinctUntilChanged(),
          tap((time) => (this.timeToLogout = time)),
          tap((time) => {
            if (time <= 0) {
              this.performLogout();
            }
          }),
          filter((time) => time > 0),
        );
      }),
      shareReplay({ refCount: true }),
    );
  }

  private listenToStorageChanges(): void {
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(filter((e) => e.key === this.storageKey && e.newValue == null))
      .subscribe(() => (window.location.href = '/'));
  }

  private performLogout(): void {
    if (this.authService.isTimeoutSignOut) {
      return;
    }

    localStorage.removeItem(this.storageKey);
    this.authService.noneActiveUserTimeoutSignOut();
  }

  private storeTTL(ttl: number): void {
    localStorage.setItem(this.storageKey, new Date(Date.now() + ttl).toISOString());
  }

  private getRemainingMs(): number {
    const item = localStorage.getItem(this.storageKey);
    if (!item) {
      return 0;
    }

    const deadline = new Date(item).getTime();
    return isNaN(deadline) ? 0 : Math.max(deadline - Date.now(), 0);
  }
}
